/**
 * @class tt
 */

import mapboxGl from 'mapbox-gl';
import { Marker } from './marker/Marker';
import { NavigationControl } from './NavigationControl';

const additionalMethodsList = [
    /**
     * @property Popup
     * @type Maps.Popup
     * @description Use this class to create a new popup component.
     */
    'Popup',

    /**
     * @property FullscreenControl
     * @type Maps.FullscreenControl
     * @description Use this class to create a new full screen control.
     */
    'FullscreenControl',

    /**
     * @property ScaleControl
     * @type Maps.ScaleControl
     * @description Use this class to create a new ScaleControl.
     */
    'ScaleControl',

    /**
     * @property GeolocateControl
     * @type Maps.GeolocateControl
     * @description Use this class to create a new GeolocateControl.
     */
    'GeolocateControl',

    /**
     * @property LngLat
     * @type Maps.LngLat
     * @description Use this class to create a new LngLat.
     */
    'LngLat',

    /**
     * @property LngLatBounds
     * @type Maps.LngLatBounds
     * @description Use this class to create a new LngLatBounds.
     */
    'LngLatBounds',

    /**
     * @property Point
     * @type Maps.Point
     * @description Use this class to create a new Point.
     */
    'Point',

    /**
     * @property MercatorCoordinate
     * @type Maps.MercatorCoordinate
     * @description Use this class to create a new MercatorCoordinate.
     */
    'MercatorCoordinate',

    /**
     * @property Evented
     * @type Maps.Evented
     * @description Use this class to create a new Evented.
     */
    'Evented'
];

function fixMapReference(methodName, mapboxMethod) {
    function replaceMapPointer() {
        const exposedMethodProto = mapboxMethod.prototype;
        const originalFunction = exposedMethodProto.addTo;

        exposedMethodProto.addTo = function(map) {
            // The original function needs to be called with a mapbox map instance as they require internal properties
            // provided by mapbox.
            // map.__om is the mapbox instance provided as a private field in our public map interface. When our public
            // map interface is used we need to make sure that we pass the correct mapbox map instance to this method.
            // If the addTo method is invoked by other mapbox methods we assume that it will receive the correct
            // mapbox map instance.
            return originalFunction.call(this, map.__om ? map.__om : map);
        };
    }

    switch (methodName) {
    case 'Popup':
    case 'Marker':
    case 'NavigationControl':
        replaceMapPointer();
        break;
    default:
        break;
    }

    return mapboxMethod;
}

/**
 * This function appends mapbox methods modified by us
 * @param {Object} exposedMethods
 */
function appendModifiedMethods(exposedMethods) {
    /**
     * @property Marker
     * @type Maps.Marker
     * @description Use this class to create a new marker component.
     */
    exposedMethods.Marker = fixMapReference('Marker', Marker);

    /**
     * @property NavigationControl
     * @type Maps.NavigationControl
     * @description Use this class to create a new NavigationControl.
     */
    exposedMethods.NavigationControl = fixMapReference('NavigationControl', NavigationControl);
}

function additionalMethodsObj() {
    const exposedMethods = {};

    additionalMethodsList.forEach((method) => {
        const mapboxMethod = mapboxGl[method];
        exposedMethods[method] = fixMapReference(method, mapboxMethod);
    });

    appendModifiedMethods(exposedMethods);

    return exposedMethods;
}

export default additionalMethodsObj;



// WEBPACK FOOTER //
// ./map/additionalCoreMethods/additionalCoreMethods.js